import { useMemo } from 'react';

import compact from 'lodash/compact';
import uniq from 'lodash/uniq';

import { LUXURY_ESCAPES } from '~/consts/brands';

import { useGetPropertiesByIds } from '~/hooks/apis/reservation/useGetPropertiesByIds';
import { useGetUsersSummaryByIds } from '~/hooks/apis/users/useGetUsersSummaryByIds';
import { useGetVendorsByIds } from '~/hooks/apis/vendors/useGetVendorsByIds';

import { ExtranetPendingChange } from '~/services/ExtranetService';

import {
  EMPTY_USER_DISPLAY,
  getDisplayName,
  getRoomMetadata,
  getTargetIdValue,
  getUserDisplay,
} from '../changeMetadata';
import type { ChangeMetadata } from '../types';

const EMPTY_USER_SUMMARIES = new Map<string, App.UserSummary>();

function uniqueSortedIds(values: Array<string | null | undefined>) {
  return uniq(compact(values)).sort();
}

export default function useExtranetChangeMetadata(changes: Array<ExtranetPendingChange>) {
  const vendorIds = useMemo(() => uniqueSortedIds(changes.map((row) => row.fk_vendor_id)), [changes]);
  const propertyIds = useMemo(
    () => uniqueSortedIds(changes.map((row) => getTargetIdValue(row.target_ids, 'property_id'))),
    [changes],
  );
  const userIds = useMemo(
    () => uniqueSortedIds(changes.flatMap((row) => [row.requested_by_user_id, row.reviewed_by_user_id])),
    [changes],
  );

  const { vendorMap } = useGetVendorsByIds(vendorIds);
  const { propertyMap } = useGetPropertiesByIds(propertyIds);
  const { data: userSummaries = EMPTY_USER_SUMMARIES } = useGetUsersSummaryByIds({
    userIds,
    brand: LUXURY_ESCAPES,
    enabled: userIds.length > 0,
  });

  return useMemo(() => {
    const changeMetadataMap = new Map<string, ChangeMetadata>();

    for (const row of changes) {
      const propertyId = getTargetIdValue(row.target_ids, 'property_id');
      const property = propertyId ? propertyMap.get(propertyId) : undefined;
      const requestedBy = getUserDisplay(userSummaries.get(row.requested_by_user_id));
      const reviewedBy = row.reviewed_by_user_id
        ? getUserDisplay(userSummaries.get(row.reviewed_by_user_id))
        : EMPTY_USER_DISPLAY;

      changeMetadataMap.set(row.id, {
        vendorName: getDisplayName(vendorMap.get(row.fk_vendor_id)),
        propertyName: getDisplayName(property),
        ...getRoomMetadata(property, row.target_ids),
        requestedByEmail: requestedBy.email,
        requestedByName: requestedBy.name,
        reviewedByEmail: reviewedBy.email,
        reviewedByName: reviewedBy.name,
      });
    }

    return changeMetadataMap;
  }, [changes, propertyMap, userSummaries, vendorMap]);
}
